import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

// 285: majitel smaže vybraná data firmy (po zkoušení na testovacích datech).
// Lidé, zakázky a nastavení zůstanou. Potvrzení opsáním SMAZAT.
const WORD = "SMAZAT";
const NOTE = {
  doklady: "přijaté a vydané faktury, účtenky, faktury subdodavatelů a jejich platby; číslování začne od 1",
  sklad: "zásoby a všechny pohyby; sklady samotné zůstanou",
  pokladna: "výdeje, zálohy, vklady a vypořádání",
  dochazka: "směny, denní výkazy a nepřítomnosti",
};

export function ResetDataDialog({ open, onClose }) {
  const qc = useQueryClient();
  const [parts, setParts] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPicked(new Set()); setWord(""); setParts(null);
    api.get("/company/reset-data").then((r) => setParts(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open]);

  const toggle = (k) => setPicked((cur) => { const n = new Set(cur); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const ok = picked.size > 0 && word.trim().toUpperCase() === WORD;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/company/reset-data", { parts: [...picked], confirm: word.trim() }, { timeout: 180000 });
      const n = Object.values(data.deleted || {}).reduce((s, v) => s + v, 0);
      toast.success(`Hotovo — smazáno ${n.toLocaleString("cs-CZ")} záznamů.`);
      qc.invalidateQueries();
      window.dispatchEvent(new Event("pp-doc-saved"));
      onClose?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="reset-data-dialog">
        <DialogHeader>
          <DialogTitle>Smazat data firmy</DialogTitle>
          <DialogDescription>Vyberte, co smazat. Lidé, zakázky a nastavení zůstanou.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" role="group" aria-label="Co smazat">
          {!parts && <p className="text-sm text-muted-foreground">Načítám…</p>}
          {parts?.map((p) => {
            const on = picked.has(p.key);
            return (
              <button key={p.key} type="button" role="checkbox" aria-checked={on} onClick={() => toggle(p.key)} disabled={busy}
                data-testid={`reset-part-${p.key}`}
                className={`w-full flex items-start gap-3 rounded-[14px] border px-3.5 py-3 text-left transition-colors motion-reduce:transition-none ${on ? "border-destructive bg-destructive/[0.05]" : "border-border hover:bg-muted/50"}`}>
                <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-md border-2 grid place-items-center ${on ? "bg-destructive border-destructive text-white" : "border-muted-foreground/50"}`}>
                  {on && <Check size={14} strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-[15px]">{p.label}</span>
                    <span className="text-sm tabular-nums text-muted-foreground whitespace-nowrap">{p.total.toLocaleString("cs-CZ")} záznamů</span>
                  </span>
                  <span className="block text-[13px] text-muted-foreground leading-snug">{NOTE[p.key]}</span>
                </span>
              </button>
            );
          })}
        </div>
        {picked.has("doklady") && !picked.has("pokladna") && (parts?.find((p) => p.key === "pokladna")?.total || 0) > 0 && (
          <p className="text-[13px] text-amber-600 dark:text-amber-400" data-testid="reset-hint-cash">Pokladní případy přijdou o své účtenky a budou vypadat nedoložené — zvažte smazat i Pokladnu.</p>
        )}
        <label className="block space-y-1.5">
          <span className="text-sm font-semibold">Pro potvrzení napište {WORD}. <span className="font-normal text-muted-foreground">Nejde to vrátit.</span></span>
          <Input value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off" disabled={busy}
            className="h-11 w-full min-w-0 text-base md:text-sm" data-testid="reset-word" />
        </label>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={busy} className="h-11 w-full sm:w-auto">Zpět</Button>
          <Button variant="destructive" onClick={run} disabled={!ok || busy} className="h-11 w-full sm:w-auto font-bold" data-testid="reset-confirm">
            <Trash2 size={16} /> {busy ? "Mažu…" : "Smazat vybraná data"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
